import { useEffect, useState } from "react";
import MailInputComponent from "./mailInputComponent";
import services from "../../services/services";
import { toast } from "react-toastify";
import type { NewTemplateObj } from "./mailTemplate";

/**
 * ManualGrantAddTourTemplate
 * ------------------------------------------------------------------
 * Editor for the email that fires when an admin adds a tour to an
 * existing user. Short confirmation — no credentials.
 *
 * DB: single row in redeemMail with type='manual_grant_add_tour'.
 * Seed: backend/scripts/seed-manual-grant-add-tour-mail.js.
 *
 * Variables: {{name}}, {{tourName}}
 * ------------------------------------------------------------------
 */

type Entry = {
  id: number;
  template: NewTemplateObj[];
  isEditMode: boolean;
  error: string;
};

function ManualGrantAddTourTemplate() {
  const [mailEntry, setMailEntry] = useState<Entry | null>(null);
  const [emailContents, setEmailContents] = useState<any[]>([]);
  const [reloadData, setReloadData] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const response = await services.getAllMailData("manual_grant_add_tour");
        const mailArray = Array.isArray(response) ? response : response?.data;
        if (cancelled) return;
        if (!mailArray || !Array.isArray(mailArray) || mailArray.length === 0) {
          setMailEntry(null);
        } else {
          const raw = mailArray[0];
          const template =
            typeof raw.template === "string"
              ? JSON.parse(raw.template)
              : raw.template;
          const normalisedTemplate: NewTemplateObj[] = (template || []).map(
            (t: any) => ({
              language: t.language || "",
              subject: t.subject || "",
              htmlTemplate: t.htmlTemplate || "",
              error: "",
              isRendering: false,
              id: Math.floor(Math.random() * 3000) + 1,
            })
          );
          setMailEntry({
            id: raw.id,
            template: normalisedTemplate,
            isEditMode: false,
            error: "",
          });
          setEmailContents([
            { templateId: raw.id, template: normalisedTemplate },
          ]);
        }
      } catch (err: any) {
        console.error("Error loading manual_grant_add_tour template:", err?.message);
        toast.error("Failed to load template");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [reloadData]);

  const onEmailTemplateChange = (mailData: any, elm: NewTemplateObj, type: string) => {
    setEmailContents((prev) => prev.map((temp) => {
      if (temp.templateId !== mailData.id) return temp;
      if (type === "update") {
        return { ...temp, template: temp.template.map((v: any) => v.id === elm.id ? { ...v, ...elm } : v) };
      }
      if (type === "remove") {
        return { ...temp, template: temp.template.filter((v: any) => v.id !== elm.id) };
      }
      if (type === "add") {
        return { ...temp, template: [...temp.template, elm] };
      }
      return temp;
    }));
  };

  const validateAllTemplate = (templates: NewTemplateObj[]) => templates.map((item) => {
    const doc = new DOMParser().parseFromString(item.htmlTemplate, "text/html");
    const isError = doc.querySelector("parsererror");
    return isError ? { ...item, htmlTemplate: "" } : { ...item, htmlTemplate: doc.documentElement.outerHTML };
  });

  const handleSave = async () => {
    if (!mailEntry) return;
    const emailTemps = emailContents.find((elem) => elem.templateId === mailEntry.id);
    if (!emailTemps || !Array.isArray(emailTemps.template) || emailTemps.template.length === 0) {
      toast.error("Add at least one language template before saving");
      return;
    }
    const validated = validateAllTemplate(emailTemps.template);
    if (validated.filter((t) => t.htmlTemplate === "").length > 0) {
      toast.error("One or more templates have invalid HTML");
      return;
    }
    const payload = {
      type: "manual_grant_add_tour",
      duration: "00:00",
      minElapsedTime: "00:00",
      tourlist: "",
      bundlelist: "",
      transportation: -1,
      template: validated.map(({ language, subject, htmlTemplate }) => ({ language, subject, htmlTemplate })),
    };
    try {
      await services.saveMailData(mailEntry.id, payload);
      toast.success("Template saved");
      setReloadData((prev) => !prev);
    } catch (err: any) {
      console.error("Error saving:", err?.message);
      toast.error("Failed to save template");
    }
  };

  const toggleEdit = () => {
    if (!mailEntry) return;
    setMailEntry({ ...mailEntry, isEditMode: !mailEntry.isEditMode });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="text-[#6B7280] text-sm">Loading template…</div>
      </div>
    );
  }

  if (!mailEntry) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-5 text-sm text-amber-800">
        <p className="font-semibold mb-1">No template found</p>
        <p className="mb-2">
          The database doesn't yet contain a template with <code>type='manual_grant_add_tour'</code>. Run the seed script on the server:
        </p>
        <pre className="bg-white border border-amber-200 rounded px-3 py-2 text-xs overflow-x-auto">
{`cd /var/www/trippy/backend
NODE_ENV=digitalocean node scripts/seed-manual-grant-add-tour-mail.js`}
        </pre>
      </div>
    );
  }

  const currentTemplates = emailContents.find((mail) => mail.templateId === mailEntry.id)?.template || [];

  return (
    <div className="flex flex-col">
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 bg-gradient-to-r from-[#E8F8F5] to-white">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-[#6ECDC5] flex items-center justify-center text-white text-sm font-bold">➕</div>
            <div>
              <h2 className="text-lg font-semibold text-[#1a1a2e] font-roboto">Manual Grant — Tour Added</h2>
              <p className="text-xs text-[#6B7280]">Fires when an admin adds a tour to an existing user's account</p>
            </div>
          </div>
          <div className="flex gap-2">
            {!mailEntry.isEditMode ? (
              <button type="button" className="flex items-center gap-1.5 px-3 py-1.5 bg-[#E8F8F5] text-[#6ECDC5] border border-[#6ECDC5] rounded-lg text-sm font-medium hover:bg-[#6ECDC5] hover:text-white transition-colors duration-200" onClick={toggleEdit}>
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                Edit
              </button>
            ) : (
              <button type="button" className="flex items-center gap-1.5 px-3 py-1.5 bg-[#6ECDC5] text-white rounded-lg text-sm font-medium hover:bg-[#5bb8b0] transition-colors duration-200 shadow-sm" onClick={handleSave}>
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                Save
              </button>
            )}
          </div>
        </div>
        <div className="px-5 pb-5 pt-3">
          <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
            <strong>Manual Grant — Tour Added emails</strong> fire when an admin adds a tour to a user who already has an account. Short confirmation — no credentials.
            <br />
            <strong>Available variables:</strong>{" "}
            <code>{"{{name}}"}</code>, <code>{"{{tourName}}"}</code>
            <br />
            <strong>Language selection:</strong> backend picks the variant matching <code>user.language</code> (set on user create/edit), falling back to English.
          </div>
          <MailInputComponent
            key="manual-grant-add-tour-template"
            mailData={mailEntry as any}
            templateData={currentTemplates}
            onEmailTemplateChange={onEmailTemplateChange}
            name="template"
            label="Language variants"
            placeholder="Enter HTML template"
            variables="Variables: {{name}}, {{tourName}}"
          />
        </div>
      </div>
    </div>
  );
}

export default ManualGrantAddTourTemplate;
